import * as React from "react";
import parse from "html-react-parser";
import { useFileFile } from "../../hooks/useFileFile";
import { useMediaFileFile } from "../../hooks/useMediaFileFile";
import { rewriteWidgetHost } from "../../utils/widgetHost";
import "./extractRichText.scss";
export const ExtractRichText = ({ richText, extraClasses }) => {
  const [scripts, setScripts] = React.useState([]);
  const [parsedContent, setParsedContent] = React.useState(null);
  const wrapperRef = React.useRef();

  const rawFiles = useFileFile();
  const rawMedia = useMediaFileFile();
  const drupalIDs = React.useMemo(
    () => rawFiles.map((e) => e?.node?.drupal_id),
    [rawFiles]
  );
  const allFiles = React.useMemo(
    () => rawFiles.map((e) => e?.node),
    [rawFiles]
  );
  const allMediafiles = React.useMemo(
    () => rawMedia.map((e) => e?.node),
    [rawMedia]
  );

  React.useEffect(() => {
    // Exit early if no richText is provided.
    if (!richText) return;

    // Temporary array to hold <script> and <link> elements found in the HTML.
    const scriptTags = [];

    // Parse and transform the raw HTML string, extracting and converting tags as needed.
    const output = extractImage(
      drupalIDs,
      richText,
      extraClasses,
      allFiles,
      allMediafiles,
      scriptTags // Pass the empty array by reference to collect tags
    );

    // Save the transformed JSX content in state for rendering.
    setParsedContent(output);

    // Save the extracted script/link tag data in state for later DOM injection.
    setScripts(scriptTags);
  }, [richText, extraClasses, drupalIDs, allFiles, allMediafiles]);

  React.useEffect(() => {
    if (!wrapperRef.current) return;

    const loadScript = (src) =>
      new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing) {
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });

    const loadJQueryAndBootstrap = async () => {
      await loadScript("https://code.jquery.com/jquery-3.6.0.min.js");
      await loadScript("https://cdn.jsdelivr.net/npm/bootstrap@4.6.2/dist/js/bootstrap.bundle.min.js");
    };
    const loadScriptsSequentially = async () => {
      try {
        await loadJQueryAndBootstrap(); // Ensure jQuery and Bootstrap are available

        for (const tag of scripts) {
          if (tag.isLink) {
            const linkEl = document.createElement("link");
            // Rewrite any authored website-components host to the env-specific one.
            linkEl.href = rewriteWidgetHost(tag.href);
            linkEl.rel = tag.rel || "stylesheet";
            document.head.appendChild(linkEl);
          } else {
            await new Promise((resolve, reject) => {
              const scriptEl = document.createElement("script");
              scriptEl.defer = true;

              if (tag.src) {
                // Rewrite any authored website-components host to the env-specific one.
                scriptEl.src = rewriteWidgetHost(tag.src);
                scriptEl.onload = resolve;
                scriptEl.onerror = reject;
              } else if (tag.innerHTML) {
                scriptEl.text = tag.innerHTML;
                resolve();
              }

              wrapperRef.current.appendChild(scriptEl);
            });
          }
        }
      } catch (err) {
        console.error("Script injection error:", err);
      }
    };

    if (scripts.length > 0) {
      loadScriptsSequentially();
    }
  }, [scripts]); // Only re-run when `scripts` state updates

  return (
    <div className={`rich-text ${extraClasses}`} ref={wrapperRef}>
      {parsedContent}
    </div>
  );
};
function extractImage(
  drupalIDs,
  body,
  extraClasses,
  allFiles,
  allMediafiles,
  scriptTags
) {
  const output = parse(body, {
    transform: (node) => {
      if (
        node.type === "img" ||
        node.type === "drupal-entity" ||
        node.type === "drupal-media"
      ) {
        const currentFile = allFiles.find(
          (file) => node.props["data-entity-uuid"] === file?.drupal_id
        );
        let imageUrl = currentFile?.publicUrl;
        let mediaUrl;
        let alt;
        if (node.type === "drupal-media") {
          const currentMedia = allMediafiles.find(
            (media) => node.props["data-entity-uuid"] === media?.drupal_id
          );
          mediaUrl = currentMedia?.relationships?.field_media_image?.publicUrl;
          alt = currentMedia?.field_media_image?.alt;
        }
        const imgAlign = node.props["data-align"] || "";
        const height = node.props["height"];
        const width = node.props["width"];
        let src = imageUrl || mediaUrl || node.props["src"];
        if (!src) {
          console.warn("Image source is undefined, skipping rendering.");
          return null;
        }
        if (src === node.props["src"]) {
          // Check if it's a base64 data URI (from mammoth Word parsing)
          const isBase64 = /^data:image\//i.test(src);
          // Check if it's a relative path (not absolute URL)
          const isRelative = !isBase64 && !/^https?:\/\//i.test(src);
          
          if (isRelative) {
            const drupalBase = process.env.GATSBY_DRUPAL_URL;
            if (drupalBase) {
              const base = drupalBase.replace(/\/+$/, ""); // remove trailing slashes
              const path = src.replace(/^\/+/, "");        // remove leading slashes
              src = `${base}/${path}`;
            } else {
              console.warn("GATSBY_DRUPAL_URL is not defined; cannot resolve relative image path:", src);
            }
          }
          // If it's base64, use it as-is (no modification needed)
        }
        const imgStyles = {};
        if (width) {
          imgStyles.width = `${width}px`;
        } else {
          // imgStyles.width = `100%`;
        }
        if (height) {
          imgStyles.height = `auto`;
        } else {
          // imgStyles.height = `100%`;
        }
        return (
          <span className={`imgAlign-${imgAlign}`}>
            <img
              style={{
                ...(width && { width: `${imgStyles.width}` }),
                ...(height && { height: `${imgStyles.height}` }),
              }}
              loading="lazy"
              alt={node?.props?.alt || alt || "image"}
              src={`${src}`}
            />
          </span>
        );
      }
      if (node.type === "script" || node.type === "link") {
        scriptTags.push({
          src: node.props?.src,
          innerHTML: node.props?.dangerouslySetInnerHTML?.__html,
          isLink: node.type === "link",
          href: node.props?.href,
          rel: node.props?.rel,
        });
        return null; // Prevent rendering at this stage
      }
      if (node.type === "a" && node.props["data-entity-type"] === "file") {
        const currentFile = allFiles.find(
          (file) => node.props["data-entity-uuid"] === file?.drupal_id
        );
        const href = node.props["href"] || "";
        const fallbackImage = process.env.GATSBY_DRUPAL_URL + href;
        return (
          <a
            href={`${currentFile?.url || fallbackImage}`}
            target={`${node.props["target"] || ""}`}
            class={`${node.props["className"] || ""}`}
            aria-label={`${node.props["aria-label"] || ""}`}
            id={`${node.props["id"] || ""}`}
            rel={`${node.props["rel"] || ""}`}
          >{`${node.props["children"] || "Link"}`}</a>
        );
      }
      
      // Detect paragraphs that contain "caption" keyword (only for DocumentImportSimple)
      // This is for Word document captions that appear after images
      if (node.type === "p" && extraClasses && extraClasses.includes("document-import-simple")) {
        const children = node.props?.children;
        let textContent = "";
        
        // Extract text content from children recursively
        const extractText = (child) => {
          if (typeof child === "string") {
            return child;
          } else if (Array.isArray(child)) {
            return child.map(extractText).join(" ");
          } else if (child && typeof child === "object") {
            if (child.props?.children) {
              return extractText(child.props.children);
            }
            return "";
          }
          return "";
        };
        
        textContent = extractText(children);
        
        // Check if text contains "caption" (case-insensitive)
        const trimmedText = textContent.trim();
        if (trimmedText && /caption/i.test(trimmedText)) {
          const existingClassName = node.props?.className || "";
          return (
            <p
              {...node.props}
              className={`${existingClassName} image-caption-word-doc`.trim()}
              data-caption="true"
            >
              {node.props?.children}
            </p>
          );
        }
      }
      
      // Ensure embedded iframes (YouTube, Vimeo, ...) send a referrer.
      // The site is served with `Referrer-Policy: same-origin`, which strips
      // the Referer header on cross-origin iframe requests. Without it YouTube
      // cannot verify the embedding domain and rejects the player with
      // "Error 153 - Video player configuration error". Overriding the iframe's
      // referrerPolicy re-enables the referrer regardless of what markup the
      // CMS stored for the embed.
      if (node.type === "iframe") {
        return React.cloneElement(node, {
          referrerPolicy:
            node.props?.referrerPolicy || "strict-origin-when-cross-origin",
        });
      }

      return node;
    },
  });

  return output;
}
export default ExtractRichText;
